/**
 * LocalBlogTemplate - Renders blog posts from local markdown files
 *
 * This template handles blog posts stored in content/blog/{slug}/index.md
 */

import { graphql } from "gatsby"
import { getImage } from "gatsby-plugin-image"
import React, { useEffect, useState } from "react"
import VisibilitySensor from "react-visibility-sensor"
import { formatBlogDate } from "utils/formatBlogDate"

import AuthorAvatars from "components/AuthorAvatars"
import Button from "components/Button"
import FixedSubscriptionPopup from "components/FixedSubscriptionPopup"
import MarkdownContent from "components/MarkdownContent"
import { SEOHead } from "components/SEO"
import SocialShare from "components/SocialShare"
import TableOfContents from "components/TableOfContents"
import Thumbnail from "components/Thumbnail"
import { BLOG_SUBSCRIPTION_FORM_ID } from "constants/hubspotFormIds"
import {
  BLOG_SUBSCRIPTION_FORM_TITLE,
  BLOG_SUBSCRIPTION_FORM_SUBTITLE,
} from "constants/texts"
import { SIGNUP_URL } from "constants/urls"
import {
  calculateReadingTime,
  formatReadingTime,
  formatWordCount,
} from "utils/readingTime"
import {
  getCtaConfigForPost,
  htmlAstContainsCta,
  injectCtaIntoAst,
} from "utils/blogCtaInjection"
import {
  generateArticleSchema,
  generateBreadcrumbSchema,
} from "utils/structuredData"
import * as css from "./BlogTemplate.module.css"
import NewsletterSection from "components/Blocks/NewsletterSection"
import NewsletterModal from "components/Blocks/NewsletterSection/NewsletterModal"
import Section from "components/Layout/Section"
import SectionHeader from "components/Layout/SectionHeader"
import Layout from "components/ui/Layout"

function LocalBlogTemplate({ data, location: { pathname } }) {
  const { markdownRemark } = data
  const { frontmatter, htmlAst, html } = markdownRemark

  const {
    title,
    slug,
    author,
    category,
    date: rawDate,
    thumbnail: thumbnailPath,
    thumbnailAlt,
    toc,
    noCta,
  } = frontmatter

  // Format date for display
  const date = formatBlogDate(rawDate)

  // Word count and reading time
  const wordCount = markdownRemark.wordCount?.words || 0
  const readingTimeMinutes = calculateReadingTime(wordCount)
  const readingTimeText = formatReadingTime(readingTimeMinutes)
  const wordCountText = formatWordCount(wordCount)

  // Determine if TOC should show:
  // - toc: true → always show
  // - toc: false → never show
  // - toc: "auto" or undefined → show if word count > 5000
  const AUTO_TOC_WORD_THRESHOLD = 5000
  const showToc =
    toc === true ||
    (toc !== false && toc !== "false" && wordCount > AUTO_TOC_WORD_THRESHOLD)

  // Get thumbnail image data — same resolver used by the blog listing and OG
  // tags, so the hero is always consistent across all three surfaces
  const thumbnailImage = data.markdownRemark.thumbnailImage?.childImageSharp
    ? getImage(data.markdownRemark.thumbnailImage.childImageSharp)
    : null

  // External thumbnail URL (for absolute URLs like http:// or //)
  const thumbnailUrl =
    thumbnailPath?.startsWith("http") || thumbnailPath?.startsWith("//")
      ? thumbnailPath
      : null

  // Auto-inject inline CTA at ~20% depth if the post doesn't already have one.
  // Opt out via `noCta: true` in frontmatter — used for thought-leadership
  // posts where the conversion card breaks the read.
  const finalHtmlAst = React.useMemo(() => {
    if (!htmlAst || noCta || htmlAstContainsCta(htmlAst)) return htmlAst
    const ctaConfig = getCtaConfigForPost(category, slug)
    if (!ctaConfig) return htmlAst
    return injectCtaIntoAst(htmlAst, ctaConfig)
  }, [htmlAst, category, slug, noCta])

  const [openSubscription, setOpenSubscription] = useState(false)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const popupDom = document.getElementById("subscription-popup")
    if (popupDom) {
      if (isVisible) popupDom.style.transform = "translate(-50%, 0%)"
      else popupDom.style.transform = "translate(-50%, 150%)"
    }
  }, [isVisible])

  // Build thumbnail object for Thumbnail component
  const thumbnail = thumbnailImage
    ? { gatsbyImageData: thumbnailImage }
    : thumbnailUrl
      ? { file: { url: thumbnailUrl } }
      : null

  return (
    <Layout pageContentType="blog-post" pageName={slug} pagePath={pathname}>
      <Section>
        <div className={showToc ? css.wrapper : css.wrapperNoToc}>
          {showToc && (
            <aside className={css.sidebar}>
              <TableOfContents htmlAst={finalHtmlAst} />
            </aside>
          )}
          <article className={css.article}>
            {thumbnail && (
              <Thumbnail
                thumbnail={thumbnail}
                alt={thumbnailAlt || title}
                className={css.thumbnail}
              />
            )}
            <div className={css.category}>{category}</div>
            <SectionHeader title={title} level="h1" className={css.title} />
            <div className={css.details}>
              <div className={css.meta}>
                <AuthorAvatars author={author} />
                <div>
                  <time dateTime={rawDate}>{date}</time>
                </div>
                <div>{readingTimeText}</div>
                <div>{wordCountText}</div>
              </div>
              <div className={css.tools}>
                <SocialShare title={title} />
                <Button
                  className={css.subscribe}
                  color="lightBlue"
                  onClick={() => setOpenSubscription(true)}
                >
                  Subscribe
                </Button>
              </div>
            </div>
            <MarkdownContent
              className="prose prose-dark-grey"
              htmlAst={finalHtmlAst}
              fallbackHtml={html}
            />
          </article>
        </div>
      </Section>
      <VisibilitySensor onChange={setIsVisible}>
        <div className={css.visibilitySensor} />
      </VisibilitySensor>
      <NewsletterSection
        heading="Let's get visual. Try Preset today."
        darkTheme
        ctaButton={{
          buttonLabel: "Start for free",
          trackingName: `Start for free (Footer)_Blog:${slug}`,
          url: SIGNUP_URL,
          color: "white",
          roundedCorner: true,
        }}
      />
      {openSubscription && (
        <NewsletterModal
          formId={BLOG_SUBSCRIPTION_FORM_ID}
          closeModal={() => setOpenSubscription(false)}
          trackSubmit={title}
          title={BLOG_SUBSCRIPTION_FORM_TITLE}
          subtitle={BLOG_SUBSCRIPTION_FORM_SUBTITLE}
          thankYouTitle="Thank you!"
          thankYouSubtitle=""
        />
      )}
      <FixedSubscriptionPopup />
    </Layout>
  )
}

export const pageQuery = graphql`
  query LocalBlogQuery($slug: String!) {
    markdownRemark(
      fields: { slug: { eq: $slug }, collection: { eq: "blog" } }
    ) {
      id
      html
      htmlAst
      wordCount {
        words
      }
      frontmatter {
        title
        slug
        author
        category
        date
        description
        thumbnail
        thumbnailAlt
        canonicalUrl
        toc
        noCta
      }
      thumbnailImage {
        childImageSharp {
          gatsbyImageData(quality: 80, width: 1504)
        }
      }
    }
  }
`

// Export a Head component for Gatsby Head API
export function Head({ data, location }) {
  const { frontmatter, wordCount } = data.markdownRemark
  const {
    title,
    description,
    canonicalUrl,
    thumbnail,
    author,
    date,
    category,
  } = frontmatter

  // For SEO/OG thumbnail — same image used by the listing and the post hero
  const thumbnailUrl =
    thumbnail?.startsWith("http") || thumbnail?.startsWith("//")
      ? thumbnail
      : data.markdownRemark.thumbnailImage?.childImageSharp?.gatsbyImageData
          ?.images?.fallback?.src

  // Build full URL for the article
  const siteUrl = "https://preset.io"
  const pageUrl = `${siteUrl}${location.pathname}`

  // Build absolute image URL
  const absoluteImageUrl = thumbnailUrl?.startsWith("http")
    ? thumbnailUrl
    : thumbnailUrl
      ? `${siteUrl}${thumbnailUrl}`
      : null

  // Generate Article structured data
  const articleSchema = generateArticleSchema({
    title,
    description,
    author: author || "Preset",
    datePublished: date,
    dateModified: date, // Could track this separately in frontmatter
    url: pageUrl,
    imageUrl: absoluteImageUrl,
    wordCount: wordCount?.words,
    category,
  })

  // Generate Breadcrumb structured data
  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: "Home", url: siteUrl },
    { name: "Blog", url: `${siteUrl}/blog/` },
    { name: title, url: pageUrl },
  ])

  return (
    <SEOHead
      title={title}
      description={description}
      thumbnail={thumbnailUrl}
      canonicalUrl={canonicalUrl}
      author={author}
      date={date}
      dateModified={date}
      pageUrl={pageUrl}
      pageType="article"
      structuredData={[articleSchema, breadcrumbSchema]}
    />
  )
}

export default LocalBlogTemplate
